// src/components/Header.js
import React, { useState, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { localePath, getTitle } from "../utils/content";
import { fetchPageById } from "../utils/fetchers";
import { tf } from "../utils/formation";
import BrandLogo from "./BrandLogo";

// Bootstrap Icons CDN
if (typeof document !== "undefined" && !document.querySelector('link[href*="bootstrap-icons"]')) {
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.0/font/bootstrap-icons.css";
  document.head.appendChild(link);
}

/** Page IDs that make up the CCMA mega-menu group, in display order */
const CCMA_PAGE_IDS = [12, 15, 16, 17, 18, 2, 19, 20, 21];

/**
 * Fetches every CCMA page once on mount, then returns a lookup map:
 *   { [id]: pageObject }
 * Re-renders automatically when `lang` changes because callers
 * re-derive the label with getTitle(pagesById[id], lang) each render.
 */
function useCcmaPages() {
  const [pagesById, setPagesById] = useState({});

  useEffect(() => {
    let cancelled = false;

    Promise.all(
      CCMA_PAGE_IDS.map((id) =>
        fetchPageById(id).then((page) => ({ id, page })).catch(() => ({ id, page: null }))
      )
    ).then((results) => {
      if (cancelled) return;
      const map = {};
      results.forEach(({ id, page }) => {
        if (page) map[id] = page;
      });
      setPagesById(map);
    });

    return () => { cancelled = true; };
  }, []); // fetch once; labels are re-derived from pagesById on every render

  return pagesById;
}

/** Static menu skeleton — CCMA links are built at render time using live data */
const MENU_STATIC = (t, lang) => [
  { kind: "link", to: "",             label: t("menu.home") },
  { kind: "link", to: "about",        label: t("menu.about") },
  {
    kind: "mega",
    label: t("menu.arbitrage"),
    groups: [
      {
        label: t("menu.ccma"),
        dynamic: "ccma",
        // links are injected at render time from the CCMA pages (see `items` below)
        links: [],
      },
    ],
  },
  {
    kind: "mega",
    label: tf(t, lang, "menu.repertoire"),
    groups: [
      {
        label: tf(t, lang, "menu.repertoire"),
        links: [
          { to: "encadrants", label: tf(t, lang, "menu.encadrants") },
          { to: "diplomes", label: tf(t, lang, "menu.diplomes") },
        ],
      },
    ],
  },
  { kind: "link", to: "publications", label: t("menu.publications") },
  { kind: "link", to: "ressources",   label: t("menu.ressources") },
  { kind: "link", to: "contact",      label: t("menu.contact") },
];

export default function Header() {
  const { t, i18n } = useTranslation();
  const location  = useLocation();
  const navigate  = useNavigate();

  const [openIdx,    setOpenIdx]    = useState(null); // desktop mega-menu
  // Mobile drawer accordion. Kept separate from openIdx so the desktop
  // "outside click" handler below can never close a drawer sub-menu.
  const [drawerIdx,  setDrawerIdx]  = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const navRef = useRef(null);

  const lang = i18n.language;

  // Fetch all CCMA pages once
  const pagesById = useCcmaPages();

  /**
   * Build the full menu each render.
   * getTitle(page, lang) picks the right language automatically —
   * same pattern used in PageDetail.js.
   */
  const items = MENU_STATIC(t, lang).map((item) => {
    if (item.kind !== "mega") return item;

    const groups = item.groups.map((group) => (group.dynamic !== "ccma" ? group : {
      ...group,
      links: CCMA_PAGE_IDS.map((id) => {
        const page = pagesById[id];
        return {
          to:    `page/${id}`,
          // Fall back to a thin placeholder while the fetch is in-flight
          label: page ? getTitle(page, lang, 80) : "…",
        };
      }),
    }));

    return { ...item, groups };
  });

  // ── Side-effects ──────────────────────────────────────────────────────────

  // Close everything on route change
  useEffect(() => {
    setOpenIdx(null);
    setDrawerIdx(null);
    setMobileOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  // Lock body scroll when drawer is open
  useEffect(() => {
    document.body.style.overflow = mobileOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [mobileOpen]);

  // Desktop: close mega on outside click.
  // Listens only while a desktop mega-menu is open, and only resets openIdx.
  // Before the fix, the drawer shared openIdx: a phone tap fires "mousedown"
  // before "click", and since the drawer is portaled to <body> (outside navRef)
  // this handler closed the sub-menu, removing the link before its click arrived.
  useEffect(() => {
    if (openIdx === null) return undefined;
    const handler = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) setOpenIdx(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [openIdx]);

  // Escape closes everything
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") {
        setOpenIdx(null);
        setDrawerIdx(null);
        setSearchOpen(false);
        setMobileOpen(false);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const changeLang = (newLang) => {
    let path = location.pathname;
    if (/^\/(ar|en|fr)(\/|$)/.test(path)) path = path.replace(/^\/(ar|en|fr)/, "") || "/";
    i18n.changeLanguage(newLang);
    navigate(newLang === "fr" ? path : `/${newLang}${path === "/" ? "" : path}`);
  };

  const submitSearch = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      navigate(localePath(lang, `search?keyword=${encodeURIComponent(searchTerm.trim())}`));
      setSearchTerm("");
      setSearchOpen(false);
    }
  };

  const closeDrawer = () => { setMobileOpen(false); setDrawerIdx(null); };

  // ── Portaled overlays (drawer + scrim + search) ───────────────────────────
  const portals = ReactDOM.createPortal(
    <>
      {/* Scrim */}
      <div
        className={`dajc-drawer-scrim${mobileOpen ? " is-visible" : ""}`}
        onClick={closeDrawer}
        aria-hidden="true"
      />

      {/* Drawer panel */}
      <div
        className={`dajc-drawer${mobileOpen ? " is-open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation"
      >
        {/* Drawer header row */}
        <div className="dajc-drawer__head">
          <span className="dajc-drawer__title">{t("brand.short")}</span>
          <button className="dajc-drawer__close" onClick={closeDrawer} aria-label={t("menu.close_menu")}>
            <i className="bi bi-x-lg" />
          </button>
        </div>

        {/* Links */}
        <nav className="dajc-drawer__nav">
          <ul className="dajc-drawer__list">
            {items.map((item, idx) => {
              if (item.kind === "link") {
                return (
                  <li key={idx} className="dajc-drawer__item">
                    <Link
                      to={localePath(lang, item.to)}
                      className="dajc-drawer__link"
                      onClick={closeDrawer}
                    >
                      <span>{item.label}</span>
                      <i className="bi bi-chevron-right dajc-drawer__arrow" />
                    </Link>
                  </li>
                );
              }

              // Accordion item
              const isOpen = drawerIdx === idx;
              return (
                <li key={idx} className="dajc-drawer__item">
                  <button
                    type="button"
                    className={`dajc-drawer__link dajc-drawer__link--toggle${isOpen ? " is-open" : ""}`}
                    onClick={() => setDrawerIdx((cur) => (cur === idx ? null : idx))}
                    aria-expanded={isOpen}
                  >
                    <span>{item.label}</span>
                    <i className={`bi bi-chevron-${isOpen ? "up" : "down"} dajc-drawer__arrow`} />
                  </button>

                  {isOpen && (
                    <div className="dajc-drawer__submenu">
                      {item.groups.map((group, gi) => (
                        <div key={gi} className="dajc-drawer__subgroup">
                          {group.label !== item.label && (
                            <div className="dajc-drawer__submenu-label">{group.label}</div>
                          )}
                          <ul className="dajc-drawer__sublist">
                            {group.links.map((lk, li) => (
                              <li key={li}>
                                <Link
                                  to={localePath(lang, lk.to)}
                                  className="dajc-drawer__sublink"
                                  onClick={closeDrawer}
                                >
                                  <span className="dajc-drawer__subnum">{String(li + 1).padStart(2, "0")}</span>
                                  <span>{lk.label}</span>
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Drawer footer */}
        <div className="dajc-drawer__foot">
          <div className="dajc-drawer__langs">
            {["fr", "ar", "en"].map((l) => (
              <button
                key={l}
                className={`dajc-drawer__lang-btn${lang === l ? " active" : ""}`}
                onClick={() => { changeLang(l); closeDrawer(); }}
              >
                {t(`lang.${l}`)}
              </button>
            ))}
          </div>
          <a href={`mailto:${t("contact.email_site")}`} className="dajc-drawer__email">
            <i className="bi bi-envelope" />
            {t("contact.email_site")}
          </a>
        </div>
      </div>

      {/* Search overlay */}
      {searchOpen && (
        <div className="dajc-search-overlay" onClick={() => setSearchOpen(false)}>
          <button
            className="dajc-search-overlay__close"
            onClick={() => setSearchOpen(false)}
            aria-label={t("common.close")}
          >
            <i className="bi bi-x-lg" />
          </button>
          <form
            className="dajc-search-overlay__form"
            onClick={(e) => e.stopPropagation()}
            onSubmit={submitSearch}
          >
            <input
              type="text"
              autoFocus
              placeholder={t("search.placeholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <button type="submit" aria-label={t("menu.search")}>
              <i className="bi bi-arrow-right" />
            </button>
          </form>
        </div>
      )}
    </>,
    document.body
  );

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      {/* ════════════════ HEADER BAR ════════════════ */}
      <header className="dajc-header">
        {/* Top bar */}
        <div className="dajc-header__topbar">
          <div className="container">
            <div>
              <i className="bi bi-envelope-fill" style={{ marginRight: 6 }} />
              <a href={`mailto:${t("contact.email_site")}`}>{t("contact.email_site")}</a>
            </div>
            <div className="dajc-header__topbar-right">
              <span><i className="bi bi-telephone-fill" style={{ marginRight: 6 }} /><bdi>{t("contact.phone")}</bdi></span>
              <span className="sep">|</span>
              <a href="https://www.caci.dz" target="_blank" rel="noopener noreferrer">
                {t("footer.caci_link")}
                <i className="bi bi-box-arrow-up-right" style={{ marginLeft: 6, fontSize: 11 }} />
              </a>
            </div>
          </div>
        </div>

        {/* Main bar */}
        <div className="container">
          <div className="dajc-header__main">
            {/* Brand */}
            <Link to={localePath(lang)} className="dajc-brand">
              <BrandLogo lang={lang} className="dajc-brand__seal" />
              <div className="dajc-brand__text">
                <div className="dajc-brand__name">{t("brand.short")}</div>
                <div className="dajc-brand__sub">{t("brand.parent")}</div>
              </div>
            </Link>

            {/* Desktop nav */}
            <nav className="dajc-nav" ref={navRef}>
              <ul className="dajc-nav-list">
                {items.map((item, idx) => {
                  if (item.kind === "link") {
                    return (
                      <li key={idx} className="dajc-nav-item">
                        <Link to={localePath(lang, item.to)} className="dajc-nav-link">
                          {item.label}
                        </Link>
                      </li>
                    );
                  }

                  const isOpen = openIdx === idx;
                  return (
                    <li key={idx} className={`dajc-nav-item dajc-nav-item--mega${isOpen ? " is-open" : ""}`}>
                      <button
                        type="button"
                        className="dajc-nav-link"
                        onClick={() => setOpenIdx(isOpen ? null : idx)}
                        aria-expanded={isOpen}
                      >
                        <span>{item.label}</span>
                        <i className="bi bi-chevron-down" />
                      </button>
                      <div className="dajc-mega" role="menu">
                        <div className="dajc-mega__inner">
                          {item.groups.map((group, gi) => (
                            <div key={gi} className="dajc-mega__group">
                              {group.label !== item.label && (
                                <div className="dajc-mega__group-title">
                                  <i className="bi bi-bookmark-fill" />
                                  <span>{group.label}</span>
                                </div>
                              )}
                              <ul className="dajc-mega__links">
                                {group.links.map((lk, li) => (
                                  <li key={li}>
                                    <Link to={localePath(lang, lk.to)} onClick={() => setOpenIdx(null)}>
                                      <span className="dajc-mega__num">{String(li + 1).padStart(2, "0")}</span>
                                      <span className="dajc-mega__label">{lk.label}</span>
                                      <i className="bi bi-arrow-right" />
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </nav>

            {/* Controls */}
            <div className="dajc-header__ctrls">
              <div className="dajc-lang">
                {["fr", "ar", "en"].map((l) => (
                  <button
                    key={l}
                    className={lang === l ? "active" : ""}
                    onClick={() => changeLang(l)}
                    aria-label={`Switch to ${l}`}
                  >
                    {l === "ar" ? "ع" : l.toUpperCase()}
                  </button>
                ))}
              </div>
              <button className="dajc-icon-btn" onClick={() => setSearchOpen(true)} aria-label={t("menu.search")}>
                <i className="bi bi-search" />
              </button>
              {/* Burger — shown only on mobile via CSS */}
              <button
                className="dajc-icon-btn dajc-burger"
                onClick={() => setMobileOpen(true)}
                aria-label={t("menu.open_menu")}
              >
                <i className="bi bi-list" style={{ fontSize: 22 }} />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Drawer, scrim and search are portaled to document.body */}
      {portals}
    </>
  );
}